/**
 * AI — the enemy system.
 *
 * Bakes the navigation and cover map at load, then runs squads of soldiers on a
 * time-sliced schedule inside the 120 Hz fixed step:
 *
 *   every step   aim turret, weapon timers, stance blend      (all agents)
 *   40 Hz        steering + `capsuleMove`                     (1/3 of agents)
 *   15 Hz        perception                                   (1/8 of agents)
 *   10 Hz        behaviour tree                               (1/12 of agents)
 *    5 Hz        squad blackboard, tickets, flank assignment
 *   budgeted     one A* + string-pull per fixed step, queued
 *
 * Published as `ctx.ai`. Emits `ai:spawn`, `ai:death`, `damage:dealt` and the
 * additive `ai:callout`; consumes `weapon:fire`, `ballistics:hit`,
 * `player:footstep`, `player:land`, `fx:explosion`.
 *
 * owner: ai
 */
import * as THREE from 'three';
import { makeRng, clamp } from '../core/Rand.js';
import { NavGraph, AGENT_RADIUS, AGENT_HEIGHT } from './Nav.js';
import { Agent, MAX_SHOT_RANGE } from './Agent.js';
import { Squad } from './Squad.js';
import { createProxyPool } from './Proxy.js';
import { AIDebug } from './Debug.js';
import { castRay, newRayResult } from './Ray.js';
import { registerAIShots } from './Shots.js';
import { createCharacterSystem } from '../characters/Characters.js';   // [characters bootstrap]

const _v = new THREE.Vector3();
const _v2 = new THREE.Vector3();
const _a = new THREE.Vector3();
const _b = new THREE.Vector3();
const _cp = new THREE.Vector3();
const _cq = new THREE.Vector3();
const _end = new THREE.Vector3();
const _negDir = new THREE.Vector3();
const _loco = { velocity: null, grounded: true, crouched: false, stance: 'stand' };
const _dir = new THREE.Vector3();
const _q = new THREE.Quaternion();
const _e = new THREE.Euler();
const _shotRay = newRayResult();
const _ringRay = newRayResult();
const DOWN = new THREE.Vector3(0, -1, 0);

const VO = {
  contact: 'vo_contact', moving: 'vo_moving', flanking: 'vo_flanking',
  reloading: 'vo_reloading', grenade: 'vo_grenade', suppressing: 'vo_suppressing',
  falling_back: 'vo_falling_back', panic: 'vo_panic', contact_lost: 'vo_contact_lost',
  what_was_that: 'vo_what_was_that', flushing: 'vo_flushing', man_down: 'vo_man_down',
};
const GESTURE = {
  contact: 'point', flanking: 'point', moving: 'point', grenade: 'throw',
  reloading: 'reload', falling_back: 'point', suppressing: 'fire',
};

const now = () => (typeof performance !== 'undefined' && performance.now)
  ? performance.now() : Date.now();

/** Beyond this, an unalerted garrison does not think. */
const ACTIVATION_R2 = 42 * 42;

const HITBOX_GEO = new THREE.BoxGeometry(1, 1, 1);
const HITBOX_MAT = new THREE.MeshBasicMaterial({ visible: false });

export function createAISystem(options = {}) {
  /** @type {any} */
  let ctx = null;
  const nav = new NavGraph();
  const agents = [];
  const squads = [];
  const grenades = [];
  const ragdolls = [];
  const sounds = [];
  let proxyPool = null;
  let debug = null;
  let charactersApi = null;
  let rng = makeRng(options.seed ?? 0x5150A1);
  let frame = 0;
  let time = 0;
  let playerFiredAt = -99;
  let bakeStats = null;
  let lastError = null;
  const perf = { fixedMs: 0, avgMs: 0, samples: 0, pathMs: 0, paths: 0, peakMs: 0 };
  const pathQueue = [];
  let shotCamActive = false;
  let fxLive = true;
  let damagePlayer = true;
  /**
   * Impact/decal budget for the live tail of a fast-forwarded fight. A firefight
   * caught mid-burst should show a handful of fresh strikes, not a wall of them —
   * see the "decal blanket" note in FINDINGS.md.
   */
  let liveImpacts = 0;
  const LIVE_IMPACT_BUDGET = 14;
  const shotCamPos = new THREE.Vector3();
  const warned = new Set();

  /* ---------------------------------------------------------------- *
   * the world handle every agent talks to
   * ---------------------------------------------------------------- */
  const world = {
    get ctx() { return ctx; },
    get physics() { return ctx.physics; },
    get player() { return ctx.player; },
    nav,
    mask: 1,
    get time() { return time; },
    rng,

    requestPath(agent, urgent = false) {
      if (!agent.hasGoal) return;
      if (agent.pathPending) return;
      agent.pathPending = true;
      if (urgent) pathQueue.unshift(agent); else pathQueue.push(agent);
    },

    suppressPointFor(agent) {
      // Suppressing fire lands deliberately NEAR the player, never on them:
      // a fixed per-agent offset so the rounds walk around one patch of cover.
      const p = agent.beliefPos(_v2);
      const k = agent.id * 2.399963;
      _v.set(
        p.x + Math.cos(k) * 0.95,
        p.y + 0.85 + Math.sin(k * 1.7) * 0.45,
        p.z + Math.sin(k) * 0.95);
      return _v;
    },

    resolveShot(agent, origin, dir, baseDamage) {
      const phys = ctx.physics;
      if (!phys) return;
      const wall = castRay(phys, origin, dir, MAX_SHOT_RANGE, world.mask, _shotRay);
      const wallT = wall ? wall.distance : MAX_SHOT_RANGE;

      const end = _end.copy(origin).addScaledVector(dir, wallT);
      const P = ctx.player;
      let hitPlayer = false;

      if (P && P.feet) {
        const h = P.height ?? P.standHeight ?? 1.8;
        const r = (P.radius ?? 0.32) * 1.02;
        _a.set(P.feet.x, P.feet.y + r, P.feet.z);
        _b.set(P.feet.x, P.feet.y + h - r, P.feet.z);
        const t = rayCapsule(origin, dir, wallT, _a, _b, r);
        if (t >= 0) {
          hitPlayer = true;
          end.copy(origin).addScaledVector(dir, t);
          const headY = P.feet.y + h - 0.30;
          const head = end.y > headY;
          const dist = t;
          const falloff = dist < 26 ? 1 : Math.max(0.42, 1 - (dist - 26) / 90);
          const dmg = baseDamage * falloff * (head ? 2.05 : 1);
          _dir.copy(dir);
          // Shots fast-forward a fight with `api.run()`; the player is frozen
          // scenery there and a screenshot of a full-red damage vignette shows
          // nothing about the AI, so hits are scored but not applied.
          if (damagePlayer) {
            try { P.applyDamage(dmg, _dir); } catch { /* player may be mid-refactor */ }
          }
          if (fxLive) { try { ctx.fx?.blood?.(end, _negDir.copy(_dir).negate(), { power: 1 }); } catch { /* optional */ } }
        }
      }

      // Tracer + impact so the fight reads on screen.
      //
      // `fxLive` is off while a shot fast-forwards a fight: `api.run()` advances
      // the AI without advancing FX, so every burst of a ten-second firefight
      // would still be alive on the first rendered frame and the screenshot
      // would be one wall of smoke. Decals are skipped too — several hundred
      // of them laid down in one frame read as a grey blanket, not as scars.
      try {
        if (fxLive) {
          ctx.fx?.tracer?.(origin, end, { speed: 460 });
          if (!hitPlayer && wall && liveImpacts < LIVE_IMPACT_BUDGET) {
            liveImpacts++;
            ctx.fx?.impact?.(wall.point, wall.normal, wall.surface, { power: 0.8 });
          }
        }
      } catch { /* fx is optional */ }
      if (fxLive) { try { ctx.audio?.gunshot?.('rifle', { position: origin, own: false }); } catch { /* optional */ } }

      // Deliberately no sound event for our own fire: the squad already shares
      // what it knows through the blackboard, and one hearing raycast per agent
      // per round is the single most expensive thing this module could do.
      if (!hitPlayer) suppressNear(end, 3.2, 0.30);
    },

    spawnGrenade(agent, target) {
      if (grenades.length > 3) return;
      const phys = ctx.physics;
      if (!phys || !phys.addBody) return;
      const from = agent.eyePos(_v).clone();
      from.y += 0.1;
      _dir.copy(target).sub(from);
      const flat = Math.hypot(_dir.x, _dir.z);
      // a lobbed toss, solved for a fixed 45-degree-ish launch
      const speed = clamp(Math.sqrt(Math.max(2, flat) * 21.6 / Math.sin(2 * 0.72)), 4, 20);
      const vy = Math.sin(0.72) * speed;
      const vh = Math.cos(0.72) * speed;
      const mesh = new THREE.Mesh(
        new THREE.IcosahedronGeometry(0.055, 1),
        new THREE.MeshStandardMaterial({ color: 0x2e3a24, roughness: 0.7, emissive: 0x220000 }));
      mesh.castShadow = true;
      mesh.userData.noCollide = true;
      ctx.scene.add(mesh);
      const body = phys.addBody({
        shape: 'sphere', size: 0.11, radius: 0.055, mass: 0.4,
        restitution: 0.32, friction: 0.6, mesh,
        position: from,
        velocity: new THREE.Vector3(_dir.x / Math.max(1e-3, flat) * vh, vy, _dir.z / Math.max(1e-3, flat) * vh),
        mask: 8,
      });
      /**
       * The telegraph: a ring on the GROUND under the grenade, the way every COD
       * indicator works. It used to be parented to the grenade's own height, so a
       * grenade at the top of its arc drew a 1.6 m orange hoop across the middle of
       * the sky — clearly visible in the old ai_cover frame.
       *
       * The colour is in scene units (CONTRACTS: 1.0 == SCENE_UNIT_NITS nits). An
       * unlit 0xff5522 is ~5600 nits, which is brighter than the sun disc; RING_NITS
       * puts it at a readable marker brightness instead of a flare.
       */
      const RING_NITS = 700;
      const unit = (ctx.sky && ctx.sky.unitScale) ? ctx.sky.unitScale : 1 / 5600;
      const ringMat = new THREE.MeshBasicMaterial({
        transparent: true, opacity: 0.85, side: THREE.DoubleSide, depthWrite: false,
      });
      ringMat.color.setRGB(1.0, 0.33, 0.13).multiplyScalar(RING_NITS * unit);
      const ring = new THREE.Mesh(new THREE.RingGeometry(0.55, 0.72, 24), ringMat);
      ring.rotation.x = -Math.PI / 2;
      ring.renderOrder = 890;
      ring.visible = false;
      ctx.scene.add(ring);
      grenades.push({ body, mesh, ring, fuse: 2.6, agent });
      try { ctx.audio?.play?.('gear', { position: from, volume: 0.5 }); } catch { /* optional */ }
    },

    callout(agent, type) {
      if (!agent.squad || !agent.squad.mayCallout(type)) return;
      agent.chestPos(_v);
      const pos = _v.clone();
      try { ctx.audio?.play?.(VO[type] || `vo_${type}`, { position: pos, volume: 1 }); } catch { /* optional */ }
      try { ctx.audio?.play?.('cloth', { position: pos, volume: 0.28 }); } catch { /* optional */ }
      const g = GESTURE[type];
      if (g && agent.character && agent.character.play) {
        try { agent.character.play(g); } catch { /* proxy tolerant */ }
      }
      try { ctx.events?.emit('ai:callout', { entity: agent, type, position: pos }); } catch { /* ignore */ }
    },

    onDeath(agent, impulse, boneName) {
      try {
        ctx.events?.emit('ai:death', {
          entity: agent, name: agent.name, team: agent.team,
          hostile: agent.hostile, position: agent.pos,
        });
      } catch { /* ignore */ }
      removeHitboxes(agent);
      const mate = agent.squad ? agent.squad.nearestMate(agent) : null;
      if (mate) mate.wantCallout('man_down');

      const c = agent.character;
      if (c && typeof c.die === 'function' && !c.isProxy) {
        try { c.die(impulse, boneName); return; } catch { /* fall through to a ragdoll */ }
      }
      if (c) { try { c.setVisible ? c.setVisible(false) : (c.visible = false); } catch { /* ignore */ } }
      spawnRagdoll(agent, impulse, boneName);
    },
  };

  /* ---------------------------------------------------------------- *
   * helpers
   * ---------------------------------------------------------------- */

  /**
   * Queue a world sound. The queue is short and drained a couple of events per
   * fixed step: hearing costs one raycast per agent per event, so it has to be
   * rationed rather than run on every impact.
   */
  function emitSound(position, radius, loud, hostile = true) {
    if (sounds.length >= 8) sounds.shift();
    sounds.push({
      position: position.clone ? position.clone() : new THREE.Vector3().copy(position),
      radius, loud, hostile,
    });
  }

  function suppressNear(point, radius, amount) {
    const r2 = radius * radius;
    for (const a of agents) {
      if (!a.alive) continue;
      if (a.pos.distanceToSquared(point) < r2) a.suppressedBy = Math.min(1, a.suppressedBy + amount);
    }
  }

  function makeHitboxes(agent) {
    const phys = ctx.physics;
    if (!phys || !phys.addBody) return;
    const spec = [
      { zone: 'head', sx: 0.26, sy: 0.28, sz: 0.26, y: () => agent.height - 0.14 },
      { zone: 'chest', sx: 0.50, sy: 0.68, sz: 0.34, y: () => agent.height - 0.60 },
      { zone: 'leg', sx: 0.44, sy: 0.86, sz: 0.34, y: () => agent.height - 1.30 },
    ];
    for (const s of spec) {
      const mesh = new THREE.Mesh(HITBOX_GEO, HITBOX_MAT);
      mesh.visible = false;
      mesh.userData.entity = agent;
      mesh.userData.hitZone = s.zone;
      mesh.userData.surface = 'flesh';
      mesh.userData.noCollide = true;
      const body = phys.addBody({
        shape: 'box', size: { x: s.sx, y: s.sy, z: s.sz }, mass: 0,
        position: new THREE.Vector3(agent.pos.x, agent.pos.y + s.y(), agent.pos.z),
        mesh, mask: phys.MASK ? phys.MASK.CHARACTER : 4,
        collidesWith: 0,
        userData: { surface: 'flesh', entity: agent, hitZone: s.zone },
      });
      if (body) agent.hitboxes.push({ body, spec: s });
    }
  }

  function removeHitboxes(agent) {
    const phys = ctx.physics;
    for (const h of agent.hitboxes) {
      try { phys?.removeBody?.(h.body); } catch { /* ignore */ }
    }
    agent.hitboxes.length = 0;
  }

  function syncHitboxes(agent) {
    if (agent.hitboxes.length === 0) { if (agent.alive) makeHitboxes(agent); return; }
    // another module may have cleared the body world (the physics shots do);
    // rebuild rather than silently becoming unshootable
    if (agent.hitboxes[0].body.removed) { agent.hitboxes.length = 0; makeHitboxes(agent); return; }
    _e.set(0, agent.aimYaw, 0, 'YXZ');
    _q.setFromEuler(_e);
    for (const h of agent.hitboxes) {
      const b = h.body;
      b.position.set(agent.pos.x + agent._leanX(), agent.pos.y + h.spec.y(), agent.pos.z + agent._leanZ());
      b.quaternion.copy(_q);
      b.updateDerived();
    }
  }

  function spawnRagdoll(agent, impulse, boneName) {
    const phys = ctx.physics;
    if (!phys || !phys.makeRagdoll) return;
    while (ragdolls.length >= 6) {
      const old = ragdolls.shift();
      try { old.dispose(); } catch { /* ignore */ }
    }
    _e.set(0, agent.aimYaw + Math.PI, 0, 'YXZ');
    _q.setFromEuler(_e);
    let r = null;
    try {
      r = phys.makeRagdoll({
        position: [agent.pos.x, agent.pos.y + 0.95, agent.pos.z],
        quaternion: _q.clone(),
        velocity: [impulse.x * 0.06, 0.6, impulse.z * 0.06],
        angularKick: 0.5,
        seed: 3300 + agent.id * 131,
        mask: phys.MASK ? phys.MASK.WORLD : 1,
      });
    } catch { r = null; }
    if (!r) return;
    try {
      const vis = phys.makeRagdollVisual(r);
      if (vis) { ctx.scene.add(vis); r._visualRoot = vis; }
    } catch { /* the ragdoll still simulates without a visual */ }
    try { r.applyImpulse(boneName === 'head' ? 'head' : 'spine', impulse); } catch { /* ignore */ }
    agent.ragdoll = r;
    ragdolls.push(r);
  }

  function clearRagdolls() {
    for (const r of ragdolls) { try { r.dispose(); } catch { /* ignore */ } }
    ragdolls.length = 0;
  }

  /* ---------------------------------------------------------------- *
   * spawning
   * ---------------------------------------------------------------- */

  function spawnAgent(opts = {}) {
    const a = new Agent(world, opts);
    // drop onto the navmesh so an agent can never start inside geometry
    if (nav.ready) {
      const n = nav.nearest(a.pos, 10);
      if (n >= 0) nav.position(n, a.pos);
    }
    a.character = makeCharacter(a, opts);
    agents.push(a);
    makeHitboxes(a);
    // The UI reads `entity.position` / `.name` / `.team` / `.hostile` straight off
    // this payload (HUD._maybeAlly, Markers, Killfeed). Before this the entity
    // carried none of them, so `_maybeAlly` bailed on the missing `position` and
    // every marker and blip in the game was placeholder data.
    try {
      ctx.events?.emit('ai:spawn', {
        entity: a, name: a.name, team: a.team, hostile: a.hostile, position: a.pos,
      });
    } catch { /* ignore */ }
    return a;
  }

  function makeCharacter(agent, opts) {
    // the CHARACTERS team's rig if it has landed, our proxy otherwise
    if (charactersApi && typeof charactersApi.spawn === 'function') {
      try {
        // Bounded seed on purpose. The characters module caches one geometry AND one
        // four-material set per distinct loadout shape and never evicts either, and
        // agent ids increase for ever (every `resetFight` in every shot mints new
        // ones). An unbounded seed therefore meant a fresh 15 k-triangle buffer and
        // four more materials per spawn — 23 shapes and 92 materials after a single
        // capture run. Twelve silhouettes is more variety than a squad needs.
        const c = charactersApi.spawn({
          position: agent.pos.clone(), yaw: agent.yaw, team: 'opfor',
          seed: 0xC0DE01 + (agent.id % 12) * 7919,
          variant: opts.variant ?? (agent.id % 3), weapon: 'rifle',
        });
        if (c) return c;
      } catch (e) { warnOnce('characters.spawn failed, using the proxy body', e); }
    }
    if (!proxyPool) return null;
    return proxyPool.spawn({ position: agent.pos.clone(), yaw: agent.yaw });
  }

  function despawn(agent) {
    removeHitboxes(agent);
    if (agent.character) { try { agent.character.dispose(); } catch { /* ignore */ } }
    agent.character = null;
    const i = agents.indexOf(agent);
    if (i >= 0) agents.splice(i, 1);
    if (agent.squad) agent.squad.remove(agent);
  }

  function clearAll() {
    for (const a of agents.slice()) despawn(a);
    agents.length = 0;
    squads.length = 0;
    clearRagdolls();
    for (const g of grenades.slice()) disposeGrenade(g);
    grenades.length = 0;
    sounds.length = 0;
    pathQueue.length = 0;
  }

  function disposeGrenade(g) {
    try { ctx.physics?.removeBody?.(g.body); } catch { /* ignore */ }
    if (g.mesh) { g.mesh.removeFromParent(); g.mesh.geometry.dispose(); g.mesh.material.dispose(); }
    if (g.ring) { g.ring.removeFromParent(); g.ring.geometry.dispose(); g.ring.material.dispose(); }
    const i = grenades.indexOf(g);
    if (i >= 0) grenades.splice(i, 1);
  }

  /**
   * Spawn a squad. `points` are world positions; anything missing falls back to
   * `ctx.level.enemySpawns` jittered along the navmesh.
   */
  function spawnSquad(opts = {}) {
    const count = opts.count ?? 5;
    const sq = new Squad(squads.length + 1, ctx, rng);
    squads.push(sq);
    const pts = opts.points || pickSpawnPoints(count, opts.around, opts.spread ?? 7);
    for (let i = 0; i < count; i++) {
      const p = pts[i % pts.length];
      const a = spawnAgent({
        position: p,
        yaw: opts.yaw ?? 0,
        difficulty: opts.difficulty || (i === 0 ? 'veteran' : 'regular'),
        seed: (opts.seed ?? 0x51A1) + i * 7919,
        patrol: opts.patrol ? buildPatrol(p, opts.patrol) : [],
      });
      sq.add(a);
    }
    return sq;
  }

  function pickSpawnPoints(count, around, spread) {
    const out = [];
    const base = around ? [around] : (ctx.level?.enemySpawns || [new THREE.Vector3()]);
    for (let i = 0; i < count; i++) {
      const b = base[i % base.length];
      const p = new THREE.Vector3(b.x, b.y, b.z);
      if (around || base.length < count) {
        const a = (i / count) * Math.PI * 2;
        p.x += Math.cos(a) * spread * (0.5 + rng() * 0.5);
        p.z += Math.sin(a) * spread * (0.5 + rng() * 0.5);
      }
      if (nav.ready) {
        const n = nav.nearest(p, 12);
        if (n >= 0) nav.position(n, p);
      } else p.y -= 1.65;
      out.push(p);
    }
    return out;
  }

  function buildPatrol(origin, radius) {
    const pts = [];
    if (!nav.ready) return pts;
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * Math.PI * 2 + rng() * 0.6;
      _v.set(origin.x + Math.cos(a) * radius, origin.y, origin.z + Math.sin(a) * radius);
      const n = nav.nearest(_v, 8);
      if (n >= 0) pts.push(nav.position(n));
    }
    return pts;
  }

  /* ---------------------------------------------------------------- *
   * event wiring
   * ---------------------------------------------------------------- */

  function wireEvents() {
    const ev = ctx.events;
    if (!ev) return;
    ev.on('weapon:fire', (e) => {
      if (!e || !e.origin) return;
      playerFiredAt = time;
      emitSound(e.origin, 55, 1.0);
    });
    ev.on('player:footstep', (e) => {
      if (!e || !e.position) return;
      emitSound(e.position, 11, 0.55);
    });
    ev.on('player:land', (e) => {
      if (!e || !e.position) return;
      emitSound(e.position, 17, 0.8);
    });
    ev.on('fx:explosion', (e) => {
      if (!e || !e.position) return;
      emitSound(e.position, 70, 1.0, false);
      suppressNear(e.position, (e.radius ?? 6) * 1.6, 0.85);
    });
    ev.on('ballistics:hit', (e) => {
      if (!e || !e.point) return;
      // a round striking near a soldier is what suppression actually is
      suppressNear(e.point, 3.4, 0.42);
      emitSound(e.point, 16, 0.55);
    });
  }

  function flushSounds() {
    if (sounds.length === 0) return;
    const w = { physics: ctx.physics, mask: world.mask, time };
    const batch = Math.min(2, sounds.length);
    for (let k = 0; k < batch; k++) {
      const s = sounds.shift();
      for (const a of agents) {
        if (!a.alive) continue;
        // already in contact: nothing a noise can tell them that they do not
        // already know, and this is where the raycasts would have gone
        if (a.perception.alert >= 3 && a.perception.confidence > 0.6) continue;
        try { a.perception.hear(s, w); } catch { /* ignore */ }
      }
    }
  }

  /* ---------------------------------------------------------------- *
   * the System
   * ---------------------------------------------------------------- */

  const system = {
    name: 'ai',
    priority: 70,

    async init(c) {
      ctx = c;
      world.mask = ctx.physics?.MASK?.WORLD ?? 1;

      if (ctx.physics?.ready) { try { await ctx.physics.ready; } catch { /* carry on */ } }

      const bounds = ctx.level?.navBounds
        || new THREE.Box3(new THREE.Vector3(-36, -1, -36), new THREE.Vector3(36, 12, 36));
      if (ctx.physics) {
        try {
          bakeStats = nav.bake(ctx.physics, bounds, { cell: options.cell ?? 0.6 });
          console.log(`[ai] navmesh baked in ${bakeStats.bakeMs.toFixed(0)} ms — `
            + `${bakeStats.nodes} nodes, ${bakeStats.links} links (${bakeStats.offMesh} off-mesh), `
            + `${bakeStats.coverPoints} cover points, ${bakeStats.rays} column rays`);
        } catch (err) {
          warnOnce('navmesh bake failed', err);
        }
      }

      charactersApi = ctx.characters || null;
      if (!charactersApi) proxyPool = createProxyPool(ctx, options.capacity ?? 24);

      if (ctx.config?.debug) {
        debug = new AIDebug(ctx);
        debug.buildNav(nav);
      }

      wireEvents();

      // the resident garrison
      if (options.spawnOnBoot !== false && nav.ready) {
        const spawns = ctx.level?.enemySpawns || [];
        spawnSquad({
          count: options.count ?? 5,
          points: spawns.slice(0, 5).map((p) => p.clone()),
          patrol: 5.5,
          seed: 0x51A1,
        });
      }

      ctx.ai = api;
      registerAIShots(ctx, api);
    },

    fixed(dt, c) {
      const t0 = now();
      time += dt;
      frame++;
      try { step(dt); } catch (err) { warnOnce('ai step failed', err); }
      const ms = now() - t0;
      perf.fixedMs = ms;
      perf.samples++;
      perf.avgMs += (ms - perf.avgMs) * 0.02;
      if (ms > perf.peakMs) perf.peakMs = ms;
      void c;
    },

    update(dt, c) {
      // late binding: the CHARACTERS team may publish after our init
      if (!charactersApi && c.characters && typeof c.characters.spawn === 'function') {
        charactersApi = c.characters;
        upgradeCharacters();
      }
      try { syncCharacters(dt); } catch (err) { warnOnce('character sync failed', err); }

      // ragdoll visuals follow their bones
      for (const r of ragdolls) {
        if (!r._visualRoot || !r.visual) continue;
        for (let i = 0; i < r.bones.length; i++) {
          const m = r.visual[i];
          if (!m) continue;
          m.position.copy(r.bones[i].position);
          m.quaternion.copy(r.bones[i].quaternion);
        }
      }

      // Our debug overlay and the hidden viewmodel belong to our shots only.
      // The moment the camera moves away from where one of them parked it,
      // both are released — that is what keeps them out of every other team's
      // screenshot, whatever order the harness runs the shots in.
      if (shotCamActive) {
        const mine = c.camera.position.distanceToSquared(shotCamPos) < 1e-4;
        c.viewScene.visible = !mine;
        if (!mine) {
          shotCamActive = false;
          if (debug) debug.setEnabled(false);
        }
      }
      if (debug && debug.enabled) debug.update(agents, squads, nav);
    },

    dispose() {
      clearAll();
      if (proxyPool) proxyPool.dispose();
      if (debug) debug.dispose();
    },
  };

  /* ---------------------------------------------------------------- *
   * the time-sliced step
   * ---------------------------------------------------------------- */

  function step(dt) {
    const n = agents.length;
    if (n === 0) return;

    flushSounds();

    // --- every agent, every step ---
    for (let i = 0; i < n; i++) {
      const a = agents[i];
      try { a.fixedStep(dt); } catch (err) { warnOnce('agent fixedStep', err); }
      if (a.alive) syncHitboxes(a);
    }

    // --- movement, 40 Hz per agent ---
    const mSlot = frame % 3;
    for (let i = 0; i < n; i++) {
      if (i % 3 !== mSlot) continue;
      try { agents[i].movementStep(dt * 3, agents, i); } catch (err) { warnOnce('agent move', err); }
    }

    // --- perception, 15 Hz per agent ---
    const pw = {
      physics: ctx.physics, player: ctx.player, mask: world.mask,
      time, playerFiredAt,
    };
    // Encounter activation: a garrison the player has not approached and that
    // has never seen anything does no perception work at all. This is both the
    // usual campaign pacing trick and where most of the idle cost goes.
    const pp = ctx.player ? ctx.player.position : null;
    const pSlot = frame % 8;
    for (let i = 0; i < n; i++) {
      if (i % 8 !== pSlot) continue;
      const a = agents[i];
      if (!a.alive) continue;
      if (pp && !a.perception.everSeen && a.perception.awareness <= 0
          && a.pos.distanceToSquared(pp) > ACTIVATION_R2) continue;
      try {
        a.perception.update(dt * 8, pw);
        a._updateCover();
        if (a.squad && a.perception.confidence > 0.2) {
          a.squad.report(a, a.perception.lastKnown, a.perception.lastKnownVel,
            a.perception.confidence, a.perception.hasLOS);
        }
      } catch (err) { warnOnce('agent perception', err); }
    }

    // --- behaviour, 10 Hz per agent ---
    const bSlot = frame % 12;
    for (let i = 0; i < n; i++) {
      if (i % 12 !== bSlot) continue;
      const a = agents[i];
      try {
        a.behaviour.tick(dt * 12, time);
        if (a.calloutQueue) { world.callout(a, a.calloutQueue); a.calloutQueue = null; }
      } catch (err) { warnOnce('agent behaviour', err); }
    }

    // --- squad, 5 Hz ---
    if (frame % 24 === 0) {
      for (const sq of squads) {
        try { sq.think(dt * 24, nav); } catch (err) { warnOnce('squad think', err); }
      }
    }

    // --- one path query per step, budgeted ---
    if (pathQueue.length) {
      const a = pathQueue.shift();
      a.pathPending = false;
      if (a.alive && a.hasGoal) {
        const t0 = now();
        try {
          const res = nav.findPath(a.pos, a.goal);
          if (res.ok && res.points.length > 1) {
            a.path = res.points;
            a.pathKinds = res.kinds;
            a.pathIndex = 1;
            // a truncated or capped route ends where it ends; make that the
            // goal so `arrived()` means something and the agent re-paths there
            if (res.partial) a.goal.copy(res.points[res.points.length - 1]);
          } else {
            a.path = null;
            a.hasGoal = false;
          }
        } catch (err) { warnOnce('path query', err); a.path = null; a.hasGoal = false; }
        perf.pathMs += now() - t0;
        perf.paths++;
      }
    }

    // --- grenades ---
    for (let i = grenades.length - 1; i >= 0; i--) {
      const g = grenades[i];
      g.fuse -= dt;
      if (g.ring && g.body) {
        // Drop the marker onto whatever is under the grenade. One ray per grenade per
        // step, and there are never more than four grenades alive.
        const floor = castRay(ctx.physics, g.body.position, DOWN, 6, world.mask, _ringRay);
        if (floor) {
          g.ring.visible = true;
          g.ring.position.set(floor.point.x, floor.point.y + 0.03, floor.point.z);
        } else {
          g.ring.visible = false;
        }
        const k = 1 - clamp(g.fuse / 2.6, 0, 1);
        g.ring.scale.setScalar(0.7 + k * 1.6);
        g.ring.material.opacity = 0.35 + 0.5 * Math.abs(Math.sin(g.fuse * 14));
      }
      if (g.fuse > 0) continue;
      const p = g.body ? g.body.position.clone() : g.mesh.position.clone();
      disposeGrenade(g);
      if (fxLive) {
        try { ctx.events?.emit('fx:explosion', { position: p, radius: 5.5, power: 110 }); } catch { /* ignore */ }
        try { ctx.fx?.explosion?.(p, 5.5, 110); } catch { /* optional */ } }
      else {
        // Fast-forward. FX only ages when the ENGINE ticks, so a blast detonated
        // six seconds into a muted `api.run()` is still at t = 0 on the first
        // rendered frame: full fireball, full smoke, full screen flash and a
        // `render.flash()` on top. That is what whited out ai_squad_engaged,
        // ai_cover and ai_flank. Muting `ctx.fx.explosion` was not enough because
        // the *event* was emitted unconditionally and the FX system listens to it.
        // The simulation-visible half of the blast still happens, directly.
        emitSound(p, 70, 1.0, false);
        suppressNear(p, 5.5 * 1.6, 0.85);
      }
      const P = ctx.player;
      if (P && P.feet) {
        const d = P.feet.distanceTo(p);
        if (d < 6.5 && damagePlayer) {
          _dir.copy(P.feet).sub(p).normalize();
          try { P.applyDamage(Math.round(70 * (1 - d / 6.5)), _dir); } catch { /* ignore */ }
        }
      }
      for (const a of agents) {
        if (!a.alive) continue;
        const d = a.pos.distanceTo(p);
        if (d < 5.0) a.applyDamage(Math.round(80 * (1 - d / 5.0)), _dir.copy(a.pos).sub(p).normalize(), 'chest');
      }
    }
  }

  /** Push agent state onto whatever body each agent is wearing. */
  function syncCharacters(dt) {
    for (const a of agents) {
      const ch = a.character;
      if (!ch) continue;
      if (ch.isProxy) {
        ch.position.copy(a.pos);
        ch.lean = a.lean;
        ch.setLocomotion(Math.hypot(a.vel.x, a.vel.z), 0, a.stance === 'crouch');
        ch.setAimTarget(a.aimPoint);
        ch.setLookTarget(a.lookPoint);
        continue;
      }
      if (!a.alive) continue;      // the rig owns the corpse from here
      try {
        // We own the facing: the turret is authoritative, and the rig's own
        // auto-face cannot turn a soldier who is standing still in cover.
        if (ch.position) ch.position.copy(a.pos);
        if (ch.rotation) ch.rotation.y = a.aimYaw;
        _loco.velocity = a.vel;
        _loco.grounded = a.grounded;
        _loco.crouched = a.stance === 'crouch';
        _loco.stance = a.stance;
        ch.setLocomotion?.(_loco);
        ch.setAimTarget?.(a.aimPoint);
        ch.setLookTarget?.(a.lookPoint);
      } catch { /* one bad rig must not stop the rest */ }
    }
    if (proxyPool) proxyPool.update(dt);
  }

  function upgradeCharacters() {
    if (!charactersApi) return;
    for (const a of agents) {
      if (a.character && !a.character.isProxy) continue;
      const old = a.character;
      const fresh = makeCharacter(a, {});
      if (fresh && !fresh.isProxy) {
        a.character = fresh;
        if (old) { try { old.dispose(); } catch { /* ignore */ } }
      }
    }
    if (agents.every((a) => a.character && !a.character.isProxy) && proxyPool) {
      proxyPool.dispose();
      proxyPool = null;
    }
  }

  /* ---------------------------------------------------------------- *
   * the published API
   * ---------------------------------------------------------------- */
  const api = {
    get entities() { return agents; },
    get squads() { return squads; },
    nav,
    world,
    get debug() { return debug; },

    spawn: spawnAgent,
    spawnSquad,
    despawn,
    clear: clearAll,

    setDebug(on) { return debug ? debug.setEnabled(on) : false; },
    /** Called by our shots so the overlay dies as soon as the camera leaves. */
    _claimShotCam(pos, wantDebug) {
      shotCamPos.copy(pos);
      shotCamActive = true;
      if (debug) debug.setEnabled(!!wantDebug);
    },
    _releaseShotCam() { shotCamActive = false; if (debug) debug.setEnabled(false); },

    /**
     * Deterministic advance of the AI simulation alone (used by the shots).
     * Transient FX are muted for all but the last `liveTail` seconds, because
     * FX only age when the engine ticks — see `resolveShot`.
     */
    run(seconds, dtStep = 1 / 120, liveTail = 0.22) {
      const steps = Math.max(0, Math.round(seconds / dtStep));
      const liveFrom = steps - Math.round(liveTail / dtStep);
      damagePlayer = false;
      liveImpacts = 0;
      for (let i = 0; i < steps; i++) {
        fxLive = i >= liveFrom;
        time += dtStep; frame++;
        try { step(dtStep); } catch (err) { warnOnce('ai run step', err); }
      }
      fxLive = true;
      damagePlayer = true;
      // The rigs are only posed in update(); without this the first settle
      // frame would see every soldier teleport from its spawn to where the
      // fight actually is, and the secondary motion would snap with it.
      try { syncCharacters(1 / 60); } catch (err) { warnOnce('ai run sync', err); }
    },

    /** Reset the whole fight to a known state; a given seed always replays. */
    resetFight(seed = 0x51A1) {
      clearAll();
      rng = makeRng(seed);
      world.rng = rng;
      time = 0; frame = 0; playerFiredAt = -99;
      perf.pathMs = 0; perf.paths = 0; perf.peakMs = 0;
      liveImpacts = 0;
      nav._queries = 0; nav._queryMs = 0;
      // Every shot runs in the same page, and decals, smoke and craters do not
      // expire between shots — the later a combat shot ran, the more of the
      // previous fights it was wearing. That is the grey blanket on the ground in
      // the old ai_death frame, and it is ours to clear because we are the ones
      // fast-forwarding fights that never get rendered.
      try { ctx.fx?.reset?.(); } catch { /* fx is optional */ }
    },

    bakeStats: () => bakeStats,
    pathStats: () => nav.queryStats(),
    stats() {
      const alive = agents.filter((a) => a.alive).length;
      return {
        agents: agents.length, alive, squads: squads.length,
        fixedMs: +perf.fixedMs.toFixed(4),
        avgMs: +perf.avgMs.toFixed(4),
        peakMs: +perf.peakMs.toFixed(4),
        paths: perf.paths,
        pathAvgMs: perf.paths ? +(perf.pathMs / perf.paths).toFixed(4) : 0,
        nav: bakeStats,
        ragdolls: ragdolls.length,
        shots: agents.reduce((s, a) => s + a.shotsFired, 0),
      };
    },

    /** Diagnostics used by the verification harness. */
    fingerprint() {
      let h = 2166136261;
      const mix = (x) => {
        h ^= Math.round(x * 1000) | 0;
        h = Math.imul(h, 16777619);
      };
      for (const a of agents) {
        mix(a.pos.x); mix(a.pos.y); mix(a.pos.z);
        mix(a.aimYaw); mix(a.aimPitch);
        mix(a.health); mix(a.ammo); mix(a.alive ? 1 : 0);
      }
      return (h >>> 0).toString(16);
    },

    /** Every agent's distance to the navmesh + whether it is inside geometry. */
    validate() {
      const out = { offMesh: 0, inSolid: 0, worst: 0, agents: agents.length };
      const hh = (AGENT_HEIGHT - 2 * AGENT_RADIUS) * 0.5;
      for (const a of agents) {
        if (!a.alive) continue;
        const n = nav.nearest(a.pos, 4);
        if (n < 0) { out.offMesh++; continue; }
        const d = Math.hypot(nav.px[n] - a.pos.x, nav.pz[n] - a.pos.z);
        if (d > nav.cell * 1.6) out.offMesh++;
        if (d > out.worst) out.worst = +d.toFixed(3);
        _v.set(a.pos.x, a.pos.y + AGENT_RADIUS + hh, a.pos.z);
        if (ctx.physics.overlapCapsule(_v, hh, AGENT_RADIUS * 0.86, world.mask)) out.inSolid++;
      }
      return out;
    },
  };

  /* ---------------------------------------------------------------- *
   * [characters bootstrap] — TEMPORARY, owner: characters
   *
   * `src/main.js` is spine-owned and has no `createCharacterSystem()` import, so
   * `src/characters/**` would never be loaded and none of its shots would register.
   * Until spine adds it to main.js, the characters system rides along here. It is
   * initialised BEFORE the AI's own init so `ctx.characters` exists by the time
   * `charactersApi = ctx.characters` runs and the garrison spawns real rigs instead
   * of proxies. Nothing above this block was changed; delete the block to undo.
   * ---------------------------------------------------------------- */
  {
    const chars = createCharacterSystem();
    const charWarned = new Set();
    const guard = (label, fn) => {
      try { fn(); } catch (err) {
        if (!charWarned.has(label)) {
          charWarned.add(label);
          console.error(`[characters] ${label} failed:`, err);
        }
      }
    };
    const baseInit = system.init.bind(system);
    const baseUpdate = system.update.bind(system);
    const baseDispose = system.dispose.bind(system);
    system.init = async (c) => { guard('init', () => chars.init(c)); await baseInit(c); };
    system.update = (dt, c) => { guard('update', () => chars.update(dt, c)); baseUpdate(dt, c); };
    system.dispose = () => { guard('dispose', () => chars.dispose()); baseDispose(); };
  }

  return system;

  /* ---------------------------------------------------------------- */

  function warnOnce(label, err) {
    lastError = { label, err: String(err && err.stack || err) };
    if (warned.has(label)) return;
    warned.add(label);
    console.warn(`[ai] ${label}:`, err);
  }
  void lastError;
}

/* ------------------------------------------------------------------ *
 * ray vs capsule — how an AI bullet decides whether it hit the player
 * ------------------------------------------------------------------ */
function rayCapsule(o, d, maxT, a, b, radius) {
  const vx = b.x - a.x, vy = b.y - a.y, vz = b.z - a.z;
  const wx = o.x - a.x, wy = o.y - a.y, wz = o.z - a.z;
  const B = d.x * vx + d.y * vy + d.z * vz;
  const C = vx * vx + vy * vy + vz * vz;
  const D = d.x * wx + d.y * wy + d.z * wz;
  const E = vx * wx + vy * wy + vz * wz;
  const denom = C - B * B;
  let t;
  if (Math.abs(denom) < 1e-9) t = 0;
  else t = (B * D - E) / -denom;      // parameter along the capsule axis
  t = t < 0 ? 0 : t > 1 ? 1 : t;
  let s = B * t - D;
  s = s < 0 ? 0 : s > maxT ? maxT : s;
  _cp.set(o.x + d.x * s, o.y + d.y * s, o.z + d.z * s);
  _cq.set(a.x + vx * t, a.y + vy * t, a.z + vz * t);
  const dist = _cp.distanceTo(_cq);
  if (dist > radius) return -1;
  const back = Math.sqrt(Math.max(0, radius * radius - dist * dist));
  const hitT = Math.max(0, s - back);
  return hitT <= maxT ? hitT : -1;
}

export default createAISystem;
